// frontend/src/components/grid/productGrouping.jsx
// "Group variants by product" — the one home for every grid that offers it.
// A grid calls `useProductGrouping` once and spreads what comes back; nothing
// about grouping is wired by hand on a page. Every item grid offers it unless
// utils/gridConventions.ratchet.test.js lists the grid as flat.
//
// The tree is one level: a PRODUCT row (the backend's header row, carrying
// `child_count` and the sums of its variants' columns) above its
// VARIANT-at-location rows. Product rows are read-only — the editable rules a
// grid writes ask `isGroupRow` — and the group key is the Shopify product gid
// the backend groups on (`PRODUCT_GROUP_FIELD`); the items without one form
// the "Ungrouped items" bucket, whose gid is null and whose group key is
// `NULL_GROUP_KEY`. AG Grid takes a server-side group row's key from the
// VALUE of the column it groups by (`setRowGroupInfo` in the enterprise row
// model; `getServerSideGroupKey` serves tree data only), which is why the
// hidden group column has a valueGetter and the bucket's key is set there.
//
// Grouping is Shopify-only: on any other store `isGrouped` is false whatever
// the stored preference says, and there is no toggle to render.
//
// Column state: the hidden field column a grouped grid groups by exists only in
// grouped mode, so a column-visibility state the merchant saved while
// ungrouped never lists it — and AG Grid shows every column a restored state
// omits. useGridState's `alwaysHidden` keeps it out of sight on every path,
// which is why the grid's state props come from here too.

import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import FilterSwitch from '../common/FilterSwitch';
import { useAuth } from '../../contexts/AuthContext';
import { useTabPersistedState } from '../../tabs/useTabPersistedState';
import { useGridState } from '../../hooks/useGridState';
import { NULL_GROUP_KEY } from '../../hooks/useSsrmGrid';
import { flexColumn } from '../../utils/gridDefaults';
import { getVariantTitle } from '../../utils/variantTitle';
import { useIsShopify, productColumn } from './productIdentity';
import { PRODUCT_FIELD_DEFAULTS } from './productFields';
import ProductIdentity from './ProductIdentityView';

// The row field every grouped grid groups by; also the colId of the hidden
// group column and the key `useSsrmGrid.buildSsrmUrl` sends as `group_by`.
export const PRODUCT_GROUP_FIELD = 'shopify_product_gid';
// What a product row is called on screen, and its picture: the product's
// featured image, which every grouped endpoint puts on the header row under
// this name (api.filters.product_group_headers, reporting.rowsource).
const PRODUCT_TITLE_FIELD = 'shopify_product_title';
const PRODUCT_IMAGE_FIELD = 'image_url';

const ALWAYS_HIDDEN = [PRODUCT_GROUP_FIELD];
const NO_COL_IDS = [];

// A product (header) row, as the backend marks it. The pinned totals row is
// NOT one — it carries no `child_count` — so an editable rule that only asks
// this still has to go through utils/pinnedTotals.js.
export const isProductGroupRow = (data) => data?.child_count != null;

// The group column's value, which AG Grid makes the product row's key: the
// gid, or the null bucket's sentinel.
const groupKeyGetter = ({ data }) => (data ? data[PRODUCT_GROUP_FIELD] ?? NULL_GROUP_KEY : undefined);

// The product tree column's value on a product row. AG Grid's group cell
// renderer formats the group KEY through the grouped column's valueFormatter
// with the product row as `data`, so the key itself never reaches copy,
// export or the screen: the row's title does, or the bucket's name where the
// row has no gid. The pinned totals row carries its label on the same field
// and `pinnedGroupLabel` prints it, so it formats to nothing here.
const groupTitleFormatter = (t) => ({ data, node }) => {
  if (node?.rowPinned || !data) return '';
  return data[PRODUCT_GROUP_FIELD] == null ? t('inventory.ungrouped') : data[PRODUCT_TITLE_FIELD];
};

// What the product tree column draws on a product row, inside AG Grid's group
// cell between the chevron and the count: the product's featured image and
// title. The bucket of items without a product is not a product, so it has no
// picture and shows its name alone. AG Grid runs this on the variant rows under
// a product too, where the tree column stays empty: their identity is the
// Variant column (`variantProductColumn`), without a picture, so each
// product's is drawn once.
const ProductGroupCell = ({ data, node }) => {
  const { t } = useTranslation();
  if (!data || !node.group) return null;
  if (data[PRODUCT_GROUP_FIELD] == null) return <span>{t('inventory.ungrouped')}</span>;
  return (
    <ProductIdentity
      imageUrl={data[PRODUCT_IMAGE_FIELD]}
      name={data[PRODUCT_TITLE_FIELD]}
      showSku={false}
    />
  );
};

// The product tree column's cell on the pinned totals row: the row's label as
// plain text. AG Grid's group cell renderer draws every other row of the
// column; a pinned row is not a group and has no key for it to show, so the
// label would otherwise land in the first variant-row column, one cell to the
// right of where the flat view puts it.
const pinnedGroupLabel = ({ data }) => <span>{data?.[PRODUCT_GROUP_FIELD] ?? ''}</span>;

const groupCellRendererSelector = (params) => (
  params.node?.rowPinned
    ? { component: pinnedGroupLabel }
    : { component: 'agGroupCellRenderer' }
);

// Row id for a grouped or flat grid: product rows by gid (`group_null` for
// the bucket), leaf rows by the grid's own id fields joined with `_` (one
// field for a row with an id of its own, the pair's two for a report row).
// Stable identity matters: getRowId is an AG Grid initial property, so a new
// function on a later render cannot be applied (warning #22) — hence field
// names, not a callback, memoised on their joined spelling.
function useGroupAwareRowId(leafIdFields) {
  const key = leafIdFields.join(',');
  return useCallback((params) => (
    isProductGroupRow(params.data)
      ? `group_${params.data[PRODUCT_GROUP_FIELD] ?? 'null'}`
      : key.split(',').map((field) => params.data[field]).join('_')
  ), [key]);
}

/**
 * Everything a grid needs to group its rows by product.
 *
 * @param gridId         useGridState's id for this grid's saved column state.
 * @param preferenceKey  ui_preferences key the toggle persists to.
 * @param defaultHidden  colIds hidden for a merchant who chose nothing
 *                       (useGridState's second argument); the group field is
 *                       added here, so a grid never lists it.
 * @param pageHidden     colIds the page itself hides in its current mode (a
 *                       page switch, not the column picker): useGridState's
 *                       `alwaysHidden` with the group field. Memoised by the
 *                       caller.
 * @param leafIdFields   the row field(s) that identify a variant row.
 * @param pinned         'left' to pin the product tree and the child identity
 *                       columns with the grid's other name columns; null to
 *                       unpin them (AG Grid skips undefined, so it never
 *                       unpins a column).
 * @param movableColumns useGridState's `movableColumns`: false fixes every
 *                       column of the grid in place.
 * @param beforeToggle   async () => boolean — a guard run before the mode flips
 *                       (an unsaved-edits prompt); false keeps the mode.
 *
 * Returns:
 *   isGrouped       false on a non-Shopify store whatever the preference says
 *   toggle          the switch to render in the filter bar, null when there is none
 *   gridProps       spread onto <AgGridReact/>: the auto group column, the
 *                   row id, and the saved column state (useGridState's props)
 *   defaultColDef   spread into the grid's defaultColDef: in grouped mode no
 *                   column sorts unless it says `sortable: true` itself — the
 *                   columns a product row sums (the server orders the products
 *                   by the sum), and the product tree column by title
 *   columns(childIdentity)  the grouped identifier columns — the hidden group
 *                   field column, then the child identity column(s) the grid
 *                   passes, pinned as asked; [] when ungrouped
 *   variantProductColumn(fields)  a child identity column that is one Product
 *                   cell without its thumbnail — the product row above carries
 *                   the picture: variant title (the product name when the
 *                   variant title is a Shopify placeholder) + SKU. Field names
 *                   default to the wire's (productFields.js); a grid with
 *                   PascalCase rows passes its own, one that shows the SKU as
 *                   its own editable column passes `skuField: null`
 *   totalsLabelField(flatField)  the row field a grid's pinned totals row
 *                   carries its label on, so the label sits in the first
 *                   column of either view
 *   isGroupRow(data)  the product-row predicate for a grid's editable rules
 */
export function useProductGrouping({
  gridId,
  preferenceKey,
  defaultHidden,
  pageHidden = NO_COL_IDS,
  leafIdFields,
  pinned,
  movableColumns = true,
  beforeToggle,
}) {
  const { t } = useTranslation();
  const { userContext, updatePreference } = useAuth();
  const isShopify = useIsShopify();
  const [groupedPreference, setGroupedPreference] = useTabPersistedState(
    'isGrouped',
    !!userContext?.user?.ui_preferences?.[preferenceKey],
  );
  const isGrouped = isShopify && groupedPreference;

  const alwaysHidden = useMemo(() => [...ALWAYS_HIDDEN, ...pageHidden], [pageHidden]);
  const gridStateProps = useGridState(gridId, defaultHidden, { alwaysHidden, movableColumns });
  const getRowId = useGroupAwareRowId(leafIdFields);

  const toggleGrouping = useCallback(async () => {
    if (beforeToggle && !(await beforeToggle())) return;
    const next = !groupedPreference;
    setGroupedPreference(next);
    updatePreference(preferenceKey, next);
  }, [beforeToggle, groupedPreference, setGroupedPreference, updatePreference, preferenceKey]);

  const toggle = isShopify ? (
    <FilterSwitch
      label={t('inventory.groupByItem')}
      checked={isGrouped}
      onChange={toggleGrouping}
    />
  ) : null;

  // AG Grid rebuilds the auto column whenever `autoGroupColumnDef` changes
  // identity, so this is memoised on exactly what goes into it.
  const groupGridProps = useMemo(() => (isGrouped ? {
    autoGroupColumnDef: {
      headerName: t('inventory.columns.shopifyProductTitle'),
      pinned,
      // The floor is what keeps a product name readable; `flexColumn` takes it
      // as an argument so nothing spreads over it.
      ...flexColumn(1, 280),
      cellRendererSelector: groupCellRendererSelector,
      cellRendererParams: { suppressCount: false, innerRenderer: ProductGroupCell },
    },
  } : {}), [isGrouped, t, pinned]);

  const gridProps = useMemo(() => ({
    ...groupGridProps,
    ...gridStateProps,
    getRowId,
    serverSideSortAllLevels: true,
  }), [groupGridProps, gridStateProps, getRowId]);

  // Grouped rows sort on the server by group, so a column header that offers
  // a sort it cannot honour is switched off — except where a column insists.
  const defaultColDef = useMemo(() => (isGrouped ? { sortable: false } : {}), [isGrouped]);

  const columns = useCallback((childIdentity) => (isGrouped ? [
    {
      field: PRODUCT_GROUP_FIELD,
      headerName: t('inventory.columns.shopifyProductTitle'),
      rowGroup: true,
      hide: true,
      suppressColumnsToolPanel: true,
      valueGetter: groupKeyGetter,
      valueFormatter: groupTitleFormatter(t),
    },
    ...childIdentity.map((col) => ({ ...col, pinned })),
  ] : []), [isGrouped, t, pinned]);

  const variantProductColumn = useCallback(({
    variantField = 'shopify_variant_title',
    nameField = PRODUCT_FIELD_DEFAULTS.nameField,
    ...overrides
  } = {}) => (
    productColumn({
      headerName: t('inventory.columns.shopifyVariantTitle'),
      nameField,
      showImage: false,
      // The product name stands in for a placeholder variant title
      // (utils/variantTitle.js). The column's own field stays the name field,
      // so the colId is still a sortable serializer field.
      valueGetter: (params) => (
        params.data
          ? getVariantTitle(params.data[variantField]) || params.data[nameField]
          : undefined
      ),
      ...overrides,
    })
  ), [t]);

  // Where the pinned totals row carries its "Total" label so that it shows in
  // the FIRST column of either view: the grid's own field when flat, the group
  // field when grouped — a field the variant row's Product cell never reads,
  // so the label is printed once, by the product tree column.
  const totalsLabelField = useCallback(
    (flatField) => (isGrouped ? PRODUCT_GROUP_FIELD : flatField),
    [isGrouped],
  );

  return {
    isGrouped,
    toggle,
    gridProps,
    defaultColDef,
    columns,
    variantProductColumn,
    totalsLabelField,
    isGroupRow: isProductGroupRow,
  };
}
